import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@/contexts/AuthContext";
import { apiGetNotes, apiCreateNote } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { MessageSquare, Send, Loader2 } from "lucide-react";
import type { EmployeeProfile } from "@/data/employees";

interface Props {
  employee: EmployeeProfile;
}

export function ProfileNotesTab({ employee }: Props) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [newNote, setNewNote] = useState("");

  const { data: notes = [] } = useQuery({
    queryKey: ["notes", employee.id],
    queryFn: () => apiGetNotes(employee.id),
  });

  const createMutation = useMutation({
    mutationFn: () => apiCreateNote(employee.id, { authorId: Number(user!.id), content: newNote }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["notes", employee.id] });
      setNewNote("");
    },
  });

  const canAddNotes = user?.role.mainRole === "admin" || user?.role.mainRole === "hr" || user?.isLineManager;

  return (
    <div className="max-w-3xl space-y-4">
      {/* Add note form */}
      {canAddNotes && (
        <Card className="shadow-card">
          <CardContent className="pt-4">
            <Textarea
              placeholder={t("profile.notes.placeholder")}
              value={newNote}
              onChange={(e) => setNewNote(e.target.value)}
              className="min-h-[80px] mb-3"
            />
            <div className="flex justify-end">
              <Button
                size="sm"
                onClick={() => createMutation.mutate()}
                disabled={!newNote.trim() || createMutation.isPending}
              >
                {createMutation.isPending ? (
                  <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                ) : (
                  <Send className="h-4 w-4 mr-1" />
                )}
                {t("profile.notes.addNote")}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Notes list */}
      <Card className="shadow-card">
        <CardHeader className="pb-3">
          <CardTitle className="text-base font-semibold flex items-center gap-2">
            <MessageSquare className="h-4 w-4 text-primary" />
            {t("profile.notes.title")}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {notes.length === 0 ? (
            <p className="text-sm text-muted-foreground py-4 text-center italic">{t("profile.notes.noNotes")}</p>
          ) : (
            <div className="space-y-4">
              {notes.map((note) => (
                <div key={note.id} className="flex gap-3">
                  {note.authorAvatarColor ? (
                    <div className={`h-8 w-8 rounded-full bg-gradient-to-br ${note.authorAvatarColor} flex items-center justify-center text-white text-xs font-semibold shrink-0`}>
                      {note.authorInitials || "?"}
                    </div>
                  ) : (
                    <div className="h-8 w-8 rounded-full bg-muted flex items-center justify-center text-muted-foreground text-xs font-semibold shrink-0">
                      ?
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-sm font-medium">{note.authorName || "Unknown"}</span>
                      <span className="text-xs text-muted-foreground">
                        {new Date(note.createdAt).toLocaleDateString("en-GB", {
                          day: "numeric",
                          month: "short",
                          year: "numeric",
                          hour: "2-digit",
                          minute: "2-digit",
                        })}
                      </span>
                    </div>
                    <p className="text-sm text-foreground whitespace-pre-wrap">{note.content}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
